import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePlatform } from '../../context/PlatformContext';
import { toast } from 'react-toastify';
import { FaTimes, FaUser, FaMapMarkerAlt, FaBriefcase, FaCreditCard, FaSearch } from 'react-icons/fa';
import { getCountries, getRegions, getCouncils, getAllCouncils } from '../../data/ukGeography';
import './Auth.css';

const COUNTRIES = [
  'United Kingdom', 'United States', 'Canada', 'Australia', 'Germany', 'France', 
  'Spain', 'Italy', 'Netherlands', 'Belgium', 'Sweden', 'Norway', 'Denmark', 
  'Finland', 'Ireland', 'Austria', 'Switzerland', 'Portugal', 'Poland', 'Greece',
  'Japan', 'South Korea', 'China', 'India', 'Brazil', 'Mexico', 'Argentina',
  'South Africa', 'Nigeria', 'Kenya', 'Egypt', 'UAE', 'Saudi Arabia', 'Singapore',
  'Malaysia', 'Thailand', 'Vietnam', 'Philippines', 'Indonesia', 'New Zealand'
].sort();

const UK_COUNTRIES = ['England', 'Wales', 'Scotland', 'Northern Ireland'];

const Signup = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    publicUsername: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    subscriberType: '',
    userSubtype: 'individual',
    mobileNumber: '',
    organisation: '',
    jobRole: '',
    country: 'United Kingdom',
    homeAddress: {
      line1: '',
      line2: '',
      city: '',
      county: '',
      postcode: ''
    },
    billingAddress: {
      line1: '',
      line2: '',
      city: '',
      county: '',
      postcode: ''
    },
    billingSameAsHome: true,
    locationPreference: {
      type: 'realtime',
      ukCountry: 'England',
      region: '',
      council: ''
    },
    assignedAuthority: '',
    soleAdministrator: false,
    teamMembers: ['', '', '', '', '']
  });
  const [loading, setLoading] = useState(false);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [availableCouncils, setAvailableCouncils] = useState([]);
  const [councilSearchTerm, setCouncilSearchTerm] = useState('');
  const [allUkCouncils, setAllUkCouncils] = useState([]);
  const { signup } = useAuth();
  const { isMobileApp } = usePlatform();
  const navigate = useNavigate();

  useEffect(() => {
    if (formData.locationPreference.ukCountry) {
      const regions = getRegions(formData.locationPreference.ukCountry);
      setAvailableRegions(regions);
      if (formData.locationPreference.region && !regions.includes(formData.locationPreference.region)) {
        setFormData(prev => ({
          ...prev,
          locationPreference: { ...prev.locationPreference, region: '', council: '' }
        }));
      }
    }
  }, [formData.locationPreference.ukCountry]);

  useEffect(() => {
    if (formData.locationPreference.ukCountry && formData.locationPreference.region) {
      const councils = getCouncils(formData.locationPreference.ukCountry, formData.locationPreference.region);
      setAvailableCouncils(councils);
    } else {
      setAvailableCouncils([]);
    }
  }, [formData.locationPreference.ukCountry, formData.locationPreference.region]);

  useEffect(() => {
    const councils = [];
    UK_COUNTRIES.forEach(country => {
      const countryCouncils = getAllCouncils(country);
      councils.push(...countryCouncils.map(c => ({ name: c, country })));
    });
    setAllUkCouncils(councils);
  }, []);

  const isIndividual = formData.role === 'user';
  const maxSteps = isIndividual ? 2 : 3;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('homeAddress.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        homeAddress: { ...prev.homeAddress, [field]: value }
      }));
    } else if (name.startsWith('billingAddress.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        billingAddress: { ...prev.billingAddress, [field]: value }
      }));
    } else if (name === 'billingSameAsHome') {
      setFormData(prev => ({
        ...prev,
        billingSameAsHome: checked,
        billingAddress: checked ? { ...prev.homeAddress } : prev.billingAddress
      }));
    } else if (name === 'role') {
      setFormData(prev => ({
        ...prev,
        role: value,
        subscriberType: value === 'user' ? '' : prev.subscriberType
      }));
      setStep(1);
    } else if (name === 'soleAdministrator') {
      setFormData(prev => ({
        ...prev,
        soleAdministrator: checked
      }));
    } else if (name.startsWith('teamMember.')) {
      const index = parseInt(name.split('.')[1]);
      setFormData(prev => {
        const newTeamMembers = [...prev.teamMembers];
        newTeamMembers[index] = value;
        return { ...prev, teamMembers: newTeamMembers };
      });
    } else if (name.startsWith('locationPreference.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        locationPreference: { ...prev.locationPreference, [field]: value }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const filteredCouncils = allUkCouncils.filter(c => 
    c.name.toLowerCase().includes(councilSearchTerm.toLowerCase())
  ).slice(0, 20);

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      if (!formData.firstName || !formData.lastName) {
        toast.error('Please enter your first and last name');
        return false;
      }
      if (!formData.email) {
        toast.error('Please enter your email address');
        return false;
      }
      if (!formData.mobileNumber) {
        toast.error('Please enter your mobile number');
        return false;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return false;
      }
      return true;
    }
    
    if (stepNum === 2) {
      if (!formData.homeAddress.line1 || !formData.homeAddress.city || !formData.homeAddress.postcode) {
        toast.error('Please fill in required address fields (Address Line 1, City, Postcode)');
        return false;
      }
      if (isIndividual) {
        const locPref = formData.locationPreference;
        if (locPref.type === 'region' && !locPref.region) {
          toast.error('Please select a region for your map view');
          return false;
        }
        if (locPref.type === 'council' && (!locPref.region || !locPref.council)) {
          toast.error('Please select a region and council for your map view');
          return false;
        }
      } else {
        if (!formData.subscriberType) {
          toast.error('Please select your organisation type');
          return false;
        }
        if (!formData.organisation) {
          toast.error('Please enter your organisation name');
          return false;
        }
        if ((formData.subscriberType === 'authority' || formData.subscriberType === 'food_bank') && !formData.assignedAuthority) {
          toast.error(formData.subscriberType === 'food_bank' 
            ? 'Please select the territory area your food bank serves'
            : 'Please select the local authority area you are responsible for');
          return false;
        }
        // Validate team members - at least 1 required unless sole administrator
        if (!formData.soleAdministrator) {
          const validTeamMembers = formData.teamMembers.filter(email => email && email.trim());
          if (validTeamMembers.length === 0) {
            toast.error('Please add at least one team member email, or check the "sole administrator" option');
            return false;
          }
          // Validate email format for entered emails
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          for (const email of validTeamMembers) {
            if (!emailRegex.test(email.trim())) {
              toast.error(`Invalid email format: ${email}`);
              return false;
            }
          }
        }
      }
      return true;
    }
    
    if (stepNum === 3 && !isIndividual) {
      if (!formData.billingSameAsHome) {
        if (!formData.billingAddress.line1 || !formData.billingAddress.city || !formData.billingAddress.postcode) {
          toast.error('Please fill in required billing address fields');
          return false;
        }
      }
      return true;
    }
    
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(step)) return;

    const displayName = `${formData.firstName} ${formData.lastName}`;
    const publicUsername = formData.publicUsername.trim() || null;

    try {
      setLoading(true);
      
      const extraFields = {
        mobileNumber: formData.mobileNumber,
        homeAddress: formData.homeAddress,
        country: formData.country
      };

      if (isIndividual) {
        extraFields.locationPreference = formData.locationPreference;
        extraFields.userSubtype = formData.userSubtype || 'individual';
      } else {
        extraFields.organisation = formData.organisation;
        extraFields.jobRole = formData.jobRole;
        extraFields.billingAddress = formData.billingSameAsHome ? formData.homeAddress : formData.billingAddress;
        extraFields.billingSameAsHome = formData.billingSameAsHome;
        extraFields.subscriberType = formData.subscriberType;
        extraFields.soleAdministrator = formData.soleAdministrator;
        // Filter out empty team member emails
        extraFields.teamMembers = formData.teamMembers.filter(email => email && email.trim());
        if (formData.subscriberType === 'authority' || formData.subscriberType === 'food_bank') {
          extraFields.assignedAuthority = formData.assignedAuthority;
        }
      }

      await signup(formData.email, formData.password, displayName, formData.role, publicUsername, extraFields);
      toast.success('Account created successfully!');
      
      if (!isIndividual) {
        navigate('/subscribe');
      } else {
        navigate('/map');
      }
    } catch (error) {
      console.error('Signup error:', error);
      if (error.code === 'auth/email-already-in-use') {
        toast.error('Email is already registered');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Invalid email address');
      } else if (error.code === 'auth/weak-password') {
        toast.error('Password is too weak');
      } else {
        toast.error(error.message || 'Signup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => {
    if (isIndividual) {
      return (
        <div className="step-indicator">
          <div className={`step-item ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-circle"><FaUser /></div>
            <span>Personal</span>
          </div>
          <div className="step-line" />
          <div className={`step-item ${step >= 2 ? 'active' : ''}`}>
            <div className="step-circle"><FaMapMarkerAlt /></div>
            <span>Address</span>
          </div>
        </div>
      );
    }

    return (
      <div className="step-indicator">
        <div className={`step-item ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <div className="step-circle"><FaUser /></div>
          <span>Personal</span>
        </div>
        <div className="step-line" />
        <div className={`step-item ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <div className="step-circle"><FaBriefcase /></div>
          <span>Organisation</span>
        </div>
        <div className="step-line" />
        <div className={`step-item ${step >= 3 ? 'active' : ''}`}>
          <div className="step-circle"><FaCreditCard /></div>
          <span>Billing</span>
        </div>
      </div>
    );
  };

  const renderStep1 = () => (
    <>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="firstName">First Name *</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="First name"
            disabled={loading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="lastName">Last Name *</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Last name"
            disabled={loading}
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="publicUsername">Nickname <span className="optional-label">(Optional)</span></label>
        <input
          type="text"
          id="publicUsername"
          name="publicUsername"
          value={formData.publicUsername}
          onChange={handleChange}
          placeholder="Public display name"
          disabled={loading}
        />
        <small className="form-help">
          This will be shown publicly instead of your real name
        </small>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="email">Email Address *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            disabled={loading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="mobileNumber">Phone Number *</label>
          <input
            type="tel"
            id="mobileNumber"
            name="mobileNumber"
            value={formData.mobileNumber}
            onChange={handleChange}
            placeholder="+44 7XXX XXXXXX"
            disabled={loading}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="password">Password *</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="At least 6 characters"
            disabled={loading}
            autoComplete="new-password"
          />
        </div>
        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password *</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Re-enter password"
            disabled={loading}
            autoComplete="new-password"
          />
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-primary btn-block" onClick={nextStep} disabled={loading}>
          Next: {isIndividual ? 'Address' : 'Organisation Details'}
        </button>
      </div>
    </>
  );

  const renderStep2Individual = () => (
    <>
      <div className="form-group">
        <label htmlFor="userSubtype">What best describes you? *</label>
        <select
          id="userSubtype"
          name="userSubtype"
          value={formData.userSubtype}
          onChange={handleChange}
          disabled={loading}
        >
          <option value="individual">Individual</option>
          <option value="restaurant">Restaurant / Cafe</option>
          <option value="retail_shop">Retail Shop</option>
          <option value="event_organiser">Event Organiser</option>
          <option value="farmer">Farmer / Allotment Holder</option>
        </select>
      </div>

      <h4 className="form-section-title">Map Viewing Preferences</h4>
      <small className="form-help" style={{ marginBottom: '15px', display: 'block' }}>
        Choose how you want to view waste reports on the map
      </small>

      <div className="location-preference-options">
        <label className={`location-option ${formData.locationPreference.type === 'realtime' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="locationPreference.type"
            value="realtime"
            checked={formData.locationPreference.type === 'realtime'}
            onChange={handleChange}
            disabled={loading}
          />
          <div className="option-content">
            <strong>My Current Location</strong>
            <span>Show waste reports near your real-time GPS location</span>
          </div>
        </label>

        <label className={`location-option ${formData.locationPreference.type === 'region' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="locationPreference.type"
            value="region"
            checked={formData.locationPreference.type === 'region'}
            onChange={handleChange}
            disabled={loading}
          />
          <div className="option-content">
            <strong>Specific Region</strong>
            <span>View all waste in a region (e.g., East Midlands, South Wales)</span>
          </div>
        </label>

        <label className={`location-option ${formData.locationPreference.type === 'council' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="locationPreference.type"
            value="council"
            checked={formData.locationPreference.type === 'council'}
            onChange={handleChange}
            disabled={loading}
          />
          <div className="option-content">
            <strong>Local Council Area</strong>
            <span>View waste reports in a specific council area</span>
          </div>
        </label>
      </div>

      {(formData.locationPreference.type === 'region' || formData.locationPreference.type === 'council') && (
        <>
          <div className="form-group" style={{ marginTop: '15px' }}>
            <label htmlFor="locationPreference.ukCountry">UK Country *</label>
            <select
              id="locationPreference.ukCountry"
              name="locationPreference.ukCountry"
              value={formData.locationPreference.ukCountry}
              onChange={handleChange}
              disabled={loading}
            >
              {UK_COUNTRIES.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="locationPreference.region">Region *</label>
            <select
              id="locationPreference.region"
              name="locationPreference.region"
              value={formData.locationPreference.region}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">Select a region...</option>
              {availableRegions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
          </div>

          {formData.locationPreference.type === 'council' && formData.locationPreference.region && (
            <div className="form-group">
              <label htmlFor="locationPreference.council">Local Council *</label>
              <select
                id="locationPreference.council"
                name="locationPreference.council"
                value={formData.locationPreference.council}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="">Select a council...</option>
                {availableCouncils.map(council => (
                  <option key={council} value={council}>{council}</option>
                ))}
              </select>
            </div>
          )}
        </>
      )}

      <div className="form-group" style={{ marginTop: '20px' }}>
        <label htmlFor="country">Country *</label>
        <select
          id="country"
          name="country"
          value={formData.country}
          onChange={handleChange}
          disabled={loading}
        >
          {COUNTRIES.map(country => (
            <option key={country} value={country}>{country}</option>
          ))}
        </select>
      </div>

      <h4 className="form-section-title">Your Address</h4>
      
      <div className="form-group">
        <label htmlFor="homeAddress.line1">Address Line 1 *</label>
        <input
          type="text"
          id="homeAddress.line1"
          name="homeAddress.line1"
          value={formData.homeAddress.line1}
          onChange={handleChange}
          placeholder="Street address"
          disabled={loading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="homeAddress.line2">Address Line 2 <span className="optional-label">(Optional)</span></label>
        <input
          type="text"
          id="homeAddress.line2"
          name="homeAddress.line2"
          value={formData.homeAddress.line2}
          onChange={handleChange}
          placeholder="Apartment, suite, unit, etc."
          disabled={loading}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="homeAddress.city">City/Town *</label>
          <input
            type="text"
            id="homeAddress.city"
            name="homeAddress.city"
            value={formData.homeAddress.city}
            onChange={handleChange}
            placeholder="City or town"
            disabled={loading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="homeAddress.county">County <span className="optional-label">(Optional)</span></label>
          <input
            type="text"
            id="homeAddress.county"
            name="homeAddress.county"
            value={formData.homeAddress.county}
            onChange={handleChange}
            placeholder="County or region"
            disabled={loading}
          />
        </div>
      </div>

      <div className="form-group" style={{ maxWidth: '200px' }}>
        <label htmlFor="homeAddress.postcode">Postcode *</label>
        <input
          type="text"
          id="homeAddress.postcode"
          name="homeAddress.postcode"
          value={formData.homeAddress.postcode}
          onChange={handleChange}
          placeholder="Postcode"
          disabled={loading}
        />
      </div>

      <div className="form-actions form-actions-dual">
        <button type="button" className="btn btn-secondary" onClick={prevStep} disabled={loading}>
          Back
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </div>
    </>
  );

  const renderStep2Corporate = () => (
    <>
      <div className="form-group">
        <label htmlFor="subscriberType">Organisation Type *</label>
        <select
          id="subscriberType"
          name="subscriberType"
          value={formData.subscriberType}
          onChange={handleChange}
          disabled={loading}
        >
          <option value="">Select organisation type...</option>
          <option value="authority">Local/Public Authority</option>
          <option value="private_company">Private Waste Management Company</option>
          <option value="recycling">Waste2Wealth Recycling Business</option>
          <option value="food_bank">Food Bank & Community Fridge</option>
          <option value="charity_shop">Charity Shop</option>
        </select>
      </div>

      {(formData.subscriberType === 'authority' || formData.subscriberType === 'food_bank') && (
        <div className="form-group authority-assignment">
          <label><FaSearch /> {formData.subscriberType === 'food_bank' ? 'Assign to Territory Area *' : 'Assign to Local Authority Area *'}</label>
          <small className="form-help" style={{ marginBottom: '10px', display: 'block' }}>
            {formData.subscriberType === 'food_bank' 
              ? 'Search and select the local council area your food bank serves'
              : 'Search and select the local council area this authority is responsible for'}
          </small>
          <input
            type="text"
            placeholder="Search councils (e.g., Westminster, Manchester, Cardiff...)"
            value={councilSearchTerm}
            onChange={(e) => setCouncilSearchTerm(e.target.value)}
            disabled={loading}
            className="council-search-input"
          />
          {councilSearchTerm && filteredCouncils.length > 0 && (
            <div className="council-search-results">
              {filteredCouncils.map(c => (
                <div 
                  key={c.name} 
                  className={`council-result-item ${formData.assignedAuthority === c.name ? 'selected' : ''}`}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, assignedAuthority: c.name }));
                    setCouncilSearchTerm('');
                  }}
                >
                  <strong>{c.name}</strong>
                  <span className="council-country">{c.country}</span>
                </div>
              ))}
            </div>
          )}
          {formData.assignedAuthority && (
            <div className="selected-authority">
              Selected: <strong>{formData.assignedAuthority}</strong>
              <button 
                type="button" 
                className="clear-authority-btn"
                onClick={() => setFormData(prev => ({ ...prev, assignedAuthority: '' }))}
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="organisation">Organisation Name *</label>
          <input
            type="text"
            id="organisation"
            name="organisation"
            value={formData.organisation}
            onChange={handleChange}
            placeholder="Company or organisation name"
            disabled={loading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="jobRole">Your Role/Job Title <span className="optional-label">(Optional)</span></label>
          <input
            type="text"
            id="jobRole"
            name="jobRole"
            value={formData.jobRole}
            onChange={handleChange}
            placeholder="Your role or job title"
            disabled={loading}
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="country">Country *</label>
        <select
          id="country"
          name="country"
          value={formData.country}
          onChange={handleChange}
          disabled={loading}
        >
          {COUNTRIES.map(country => (
            <option key={country} value={country}>{country}</option>
          ))}
        </select>
      </div>

      <h4 className="form-section-title">Organisation Address</h4>
      
      <div className="form-group">
        <label htmlFor="homeAddress.line1">Address Line 1 *</label>
        <input
          type="text"
          id="homeAddress.line1"
          name="homeAddress.line1"
          value={formData.homeAddress.line1}
          onChange={handleChange}
          placeholder="Street address"
          disabled={loading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="homeAddress.line2">Address Line 2 <span className="optional-label">(Optional)</span></label>
        <input
          type="text"
          id="homeAddress.line2"
          name="homeAddress.line2"
          value={formData.homeAddress.line2}
          onChange={handleChange}
          placeholder="Building, floor, unit, etc."
          disabled={loading}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="homeAddress.city">City/Town *</label>
          <input
            type="text"
            id="homeAddress.city"
            name="homeAddress.city"
            value={formData.homeAddress.city}
            onChange={handleChange}
            placeholder="City or town"
            disabled={loading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="homeAddress.county">County <span className="optional-label">(Optional)</span></label>
          <input
            type="text"
            id="homeAddress.county"
            name="homeAddress.county"
            value={formData.homeAddress.county}
            onChange={handleChange}
            placeholder="County or region"
            disabled={loading}
          />
        </div>
      </div>

      <div className="form-group" style={{ maxWidth: '200px' }}>
        <label htmlFor="homeAddress.postcode">Postcode *</label>
        <input
          type="text"
          id="homeAddress.postcode"
          name="homeAddress.postcode"
          value={formData.homeAddress.postcode}
          onChange={handleChange}
          placeholder="Postcode"
          disabled={loading}
        />
      </div>

      <h4 className="form-section-title" style={{ marginTop: '24px' }}>Team Members</h4>
      <p className="form-help" style={{ marginBottom: '16px' }}>
        For backup and collaboration, add 1-5 colleagues who will also have access to your corporate dashboard. 
        Each team member will receive an email with login credentials.
      </p>

      <div className="team-members-section">
        {formData.teamMembers.map((email, index) => (
          <div className="form-group" key={index}>
            <label htmlFor={`teamMember.${index}`}>
              Team Member {index + 1} Email {index === 0 && !formData.soleAdministrator ? '*' : <span className="optional-label">(Optional)</span>}
            </label>
            <input
              type="email"
              id={`teamMember.${index}`}
              name={`teamMember.${index}`}
              value={email}
              onChange={handleChange}
              placeholder={`colleague${index + 1}@yourcompany.com`}
              disabled={loading || formData.soleAdministrator}
              style={{ opacity: formData.soleAdministrator ? 0.5 : 1 }}
            />
          </div>
        ))}
      </div>

      <div className="form-group checkbox-group" style={{ marginTop: '16px', padding: '16px', background: '#fef3c7', borderRadius: '8px', border: '1px solid #f59e0b' }}>
        <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <input
            type="checkbox"
            name="soleAdministrator"
            checked={formData.soleAdministrator}
            onChange={handleChange}
            disabled={loading}
            style={{ marginTop: '4px' }}
          />
          <span>
            <strong>I am the sole administrator</strong>
            <br />
            <small style={{ color: '#92400e' }}>
              I understand that without additional team members, only I can access and manage this corporate dashboard. 
              This may limit backup access and collaboration capabilities.
            </small>
          </span>
        </label>
      </div>

      <div className="form-actions form-actions-dual">
        <button type="button" className="btn btn-secondary" onClick={prevStep} disabled={loading}>
          Back
        </button>
        <button type="button" className="btn btn-primary" onClick={nextStep} disabled={loading}>
          Next: Billing Details
        </button>
      </div>
    </>
  );

  const renderStep3Billing = () => (
    <>
      <h4 className="form-section-title">Billing Address</h4>
      
      <div className="form-group checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="billingSameAsHome"
            checked={formData.billingSameAsHome}
            onChange={handleChange}
            disabled={loading}
          />
          <span className="checkbox-text">Same as Organisation Address</span>
        </label>
      </div>

      {!formData.billingSameAsHome && (
        <>
          <div className="form-group">
            <label htmlFor="billingAddress.line1">Address Line 1 *</label>
            <input
              type="text"
              id="billingAddress.line1"
              name="billingAddress.line1"
              value={formData.billingAddress.line1}
              onChange={handleChange}
              placeholder="Street address"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="billingAddress.line2">Address Line 2 <span className="optional-label">(Optional)</span></label>
            <input
              type="text"
              id="billingAddress.line2"
              name="billingAddress.line2"
              value={formData.billingAddress.line2}
              onChange={handleChange}
              placeholder="Building, floor, unit, etc."
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="billingAddress.city">City/Town *</label>
              <input
                type="text"
                id="billingAddress.city"
                name="billingAddress.city"
                value={formData.billingAddress.city}
                onChange={handleChange}
                placeholder="City or town"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="billingAddress.county">County <span className="optional-label">(Optional)</span></label>
              <input
                type="text"
                id="billingAddress.county"
                name="billingAddress.county"
                value={formData.billingAddress.county}
                onChange={handleChange}
                placeholder="County or region"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group" style={{ maxWidth: '200px' }}>
            <label htmlFor="billingAddress.postcode">Postcode *</label>
            <input
              type="text"
              id="billingAddress.postcode"
              name="billingAddress.postcode"
              value={formData.billingAddress.postcode}
              onChange={handleChange}
              placeholder="Postcode"
              disabled={loading}
            />
          </div>
        </>
      )}

      <div className="subscription-note">
        <p>After creating your account, you'll be directed to choose a subscription plan and set up payment.</p>
      </div>

      <div className="form-actions form-actions-dual">
        <button type="button" className="btn btn-secondary" onClick={prevStep} disabled={loading}>
          Back
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Creating Account...' : 'Create Account & Continue'}
        </button>
      </div>
    </>
  );

  return (
    <div className="auth-page">
      <div className="auth-container" style={{ maxWidth: isIndividual ? '500px' : '600px' }}>
        <div className="auth-card">
          <button className="auth-close-btn" onClick={() => navigate('/')} title="Close">
            <FaTimes />
          </button>

          {!isMobileApp ? (
            <div className="account-type-selector" style={{ marginBottom: '20px' }}>
              <label htmlFor="role" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Account Type *</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                disabled={loading}
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px' }}
              >
                <option value="user">Individual User (Free)</option>
                <option value="subscriber">Corporate/Organisation (Paid)</option>
              </select>
              <small style={{ display: 'block', marginTop: '6px', color: '#6b7280', fontSize: '13px' }}>
                {isIndividual 
                  ? 'Free access to report waste and view your personal dashboard' 
                  : 'Paid subscription with corporate dashboard, analytics and team features'}
              </small>
            </div>
          ) : (
            <div style={{ marginBottom: '16px', padding: '10px 14px', background: '#f0fdf4', borderRadius: '8px', fontSize: '14px', color: '#166534' }}>
              Free account — report waste and track your impact
            </div>
          )}

          <h1 className="auth-title">Create Account</h1>
          <p className="auth-subtitle">
            {isIndividual 
              ? 'Join WasteMapr.com - it\'s free!' 
              : 'Set up your corporate account'}
          </p>

          {renderStepIndicator()}

          <form onSubmit={handleSubmit} className="auth-form">
            {step === 1 && renderStep1()}
            {step === 2 && isIndividual && renderStep2Individual()}
            {step === 2 && !isIndividual && renderStep2Corporate()}
            {step === 3 && !isIndividual && renderStep3Billing()}
          </form>

          <div className="auth-footer">
            <p>
              Already have an account? <Link to="/login">Log in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
